/**
 * API Service for making backend API calls
 * All API calls should use this utility to ensure consistent base URL and authentication
 */

import { getToken } from './session';

// Base API URL from environment variable (without /api suffix)
// Example: https://localhost:58409
// The /api prefix will be added automatically for all API endpoints
 const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://ameenaiintegration.eg-insurtech.com';
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://localhost:58409';
const API_PREFIX = '/api';

export interface LoginRequest {
  userName: string;
  password: string;
  mobile?: string;
  email?: string;
}

export interface LoginResponse {
  token: string;
  userName: string;
  mobile: string;
  email: string;
}

export interface ApiError {
  message: string;
  statusCode?: number;
}

/**
 * Creates headers for API requests, automatically including the auth token if available
 */
function getAuthHeaders(includeAuth: boolean = true): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (includeAuth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
}

/**
 * Gets the full API base URL with /api prefix
 */
export function getApiBaseUrl(): string {
  return `${API_BASE_URL}${API_PREFIX}`;
}

/**
 * Generic API fetch function that automatically includes authentication token
 * All API calls should use this function to ensure consistent base URL and auth headers
 * 
 * @param endpoint - API endpoint path (e.g., '/Auth/login' or '/Policy/calculate-premium')
 * @param options - Fetch options (method, body, headers, etc.)
 * @param includeAuth - Whether to include authentication token (default: true)
 * @returns Promise<Response>
 * 
 * @example
 * const response = await apiFetch('/Policy/calculate-premium', {
 *   method: 'POST',
 *   body: JSON.stringify(data)
 * });
 */
export async function apiFetch(
  endpoint: string,
  options: RequestInit = {},
  includeAuth: boolean = true
): Promise<Response> {
  // If endpoint is already a full URL, use it as-is
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    const headers = {
      ...getAuthHeaders(includeAuth),
      ...options.headers,
    };
    return fetch(endpoint, {
      ...options,
      headers,
    });
  }
  
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${getApiBaseUrl()}${normalizedEndpoint}`;
  
  const headers = {
    ...getAuthHeaders(includeAuth),
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  return response;
}

/**
 * Makes a login request to the backend API (no auth token needed)
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  try {
    const response = await apiFetch('/Auth/login', {
      method: 'POST',
      body: JSON.stringify({
        UserName: credentials.userName,
        Password: credentials.password,
        Mobile: credentials.mobile || '',
        Email: credentials.email || '',
      }),
    }, false); // Don't include auth token for login

    if (!response.ok) {
      let errorMessage = 'Login failed';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = `Login failed with status ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return {
      token: data.token,
      userName: data.userName,
      mobile: data.mobile || '',
      email: data.email || '',
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred during login');
  }
}

